import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  userApi,
  estateApi,
  bulkVisitApi,
  propertyUnitApi,
  residentRegistrationApi,
} from "../services/api";
import {
  Plus,
  Edit,
  Trash2,
  Users,
  Building2,
  Mail,
  User,
  X,
  Search,
  UserCheck,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  MessageSquare,
} from "lucide-react";
import toast from "react-hot-toast";
import { useEstate } from "../contexts/EstateContext";

const getApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  fallbackMessage;

const buildEmptyFormData = () => ({
  name: "",
  userId: "",
  email: "",
  phone: "",
  role: "resident",
  password: "",
  apartment: "",
  estate: "",
  unitId: "",
  dateOfBirth: "",
  gender: "male",
  governmentIdNumber: "",
  cardNo: "",
  profilePhotoUrl: "",
  ownershipType: "owner",
  moveInDate: "",
  proofOfResidencyDocumentUrl: "",
  governmentIdDocumentUrl: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  occupation: "",
  employer: "",
  additionalNotes: "",
  dependentLimit: "0",
});

const UsersPage = () => {
  const { selectedEstateId: shellEstateId, setSelectedEstateId: setShellEstateId } = useEstate();
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBulkPermissionModal, setShowBulkPermissionModal] = useState(false);
  const [showBulkRequestsModal, setShowBulkRequestsModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [bulkPermissionData, setBulkPermissionData] = useState({
    approved: true,
    limit: 75,
  });
  const [rejectingRequest, setRejectingRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [formData, setFormData] = useState(buildEmptyFormData());
  const [uploadingFields, setUploadingFields] = useState({});

  // estate filtering/grouping
  const [selectedEstateId, setSelectedEstateId] = useState("");

  useEffect(() => { if (shellEstateId) setSelectedEstateId(shellEstateId); }, [shellEstateId]);

  const queryClient = useQueryClient();

  // Fetch data
  const {
    data: users,
    isLoading,
    error: usersError,
  } = useQuery("users", userApi.getAll, {
    retry: 1,
    onError: (error) => {
      console.error("Error fetching users:", error);
    },
  });

  const { data: estates, error: estatesError } = useQuery(
    "estates",
    estateApi.getAll,
    {
      retry: 1,
      onError: (error) => {
        console.error("Error fetching estates:", error);
      },
    },
  );

  const {
    data: propertyUnits = [],
    isLoading: loadingPropertyUnits,
  } = useQuery(
    ["residentPropertyUnits", formData.estate],
    () =>
      propertyUnitApi.getAll({
        estateId: formData.estate,
        status: "active",
      }),
    {
      enabled:
        showModal &&
        modalType === "create" &&
        formData.role === "resident" &&
        !!formData.estate,
      retry: 1,
      onError: (error) => {
        console.error("Error fetching property units:", error);
      },
    },
  );

  const resolveDefaultEstateId = () => {
    if (selectedEstateId) {
      return selectedEstateId;
    }

    if (Array.isArray(estates) && estates.length === 1) {
      return estates[0]._id;
    }

    return "";
  };

  const availablePropertyUnits = Array.isArray(propertyUnits)
    ? [...propertyUnits].sort((a, b) => {
      const occupancyOrder = {
        vacant: 0,
        reserved: 1,
        occupied: 2,
      };

      const aOrder = occupancyOrder[a?.occupancyStatus] ?? 99;
      const bOrder = occupancyOrder[b?.occupancyStatus] ?? 99;

      if (aOrder !== bOrder) return aOrder - bOrder;

      return (a?.unitCode || "").localeCompare(b?.unitCode || "");
    })
    : [];

  // Fetch all bulk requests
  const { data: allBulkRequests } = useQuery(
    "bulkRequests",
    bulkVisitApi.getAll,
    {
      retry: 1,
    },
  );

  // Fetch user-specific bulk requests when modal opens
  const { data: userBulkRequests, isLoading: loadingUserRequests } = useQuery(
    ["userBulkRequests", selectedUser?._id],
    () => bulkVisitApi.getByUser(selectedUser._id),
    {
      enabled: !!selectedUser && showBulkRequestsModal,
      retry: 1,
    },
  );

  // Mutations
  const createUser = useMutation(userApi.create, {
    onSuccess: (response) => {
      queryClient.invalidateQueries("users");
      const createdUserId = response?.data?.data?.userId;
      toast.success(
        createdUserId
          ? `User created. Login ID: ${createdUserId}`
          : "User created successfully"
      );
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, "Failed to create user"));
    },
  });

  const createResidentRegistration = useMutation(
    (data) => residentRegistrationApi.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("users");
        queryClient.invalidateQueries("residentRegistrations");
        queryClient.invalidateQueries("residentProfiles");
        queryClient.invalidateQueries("residentPropertyUnits");
        toast.success("Resident created and activated successfully");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(
          getApiErrorMessage(error, "Failed to create resident registration"),
        );
      },
    },
  );

  const updateUser = useMutation(
    (data) => userApi.update(selectedUser._id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("users");
        toast.success("User updated successfully");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, "Failed to update user"));
      },
    },
  );

  const deleteUser = useMutation(userApi.delete, {
    onSuccess: (response) => {
      queryClient.invalidateQueries("users");
      queryClient.invalidateQueries("residentProfiles");
      queryClient.invalidateQueries("residentRegistrations");
      queryClient.invalidateQueries("residentPropertyUnits");
      toast.success(response?.data?.message || "User removed successfully");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, "Failed to delete user"));
    },
  });

  const updateBulkPermission = useMutation(
    ({ userId, data }) => userApi.updateBulk(userId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("users");
        toast.success("Bulk visit permission updated successfully");
        setShowBulkPermissionModal(false);
        setBulkPermissionData({ approved: true, limit: 75 });
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, "Failed to update bulk permission"));
      },
    },
  );

  const approveBulkRequest = useMutation(
    (requestId) => bulkVisitApi.approve(requestId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("userBulkRequests");
        queryClient.invalidateQueries("bulkRequests");
        toast.success("Bulk request approved successfully");
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, "Failed to approve request"));
      },
    },
  );

  const rejectBulkRequest = useMutation(
    ({ requestId, reason }) => bulkVisitApi.reject(requestId, reason),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("userBulkRequests");
        queryClient.invalidateQueries("bulkRequests");
        toast.success("Bulk request rejected");
        setRejectingRequest(null);
        setRejectReason("");
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error, "Failed to reject request"));
      },
    },
  );

  const resetForm = (estateId = resolveDefaultEstateId()) => {
    setFormData({
      ...buildEmptyFormData(),
      estate: estateId,
    });
    setSelectedUser(null);
  };

  const openPropertyUnitRegister = () => {
    const estateId = formData.estate || selectedEstateId;
    if (estateId) {
      navigate(`/property-units?estateId=${estateId}`);
      return;
    }
    navigate("/property-units");
  };

  const handleCreate = () => {
    setModalType("create");
    resetForm();
    setShowModal(true);
  };

  const handleEdit = async (user) => {
    setModalType("edit");
    setSelectedUser(user);

    try {
      const fullUserData = await userApi.getById(user._id);
      console.log("Full user data:", fullUserData);
    } catch (error) {
      console.error("Error fetching full user:", error);
    }

    setFormData({
      ...buildEmptyFormData(),
      name: user.name,
      userId: user.userId || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role,
      password: "",
      apartment: user.apartment || "",
      estate: user.estate?._id || user.estateId || "",
    });
    setShowModal(true);
  };

  const handleDelete = (user) => {
    const message =
      user.role === "resident"
        ? `Remove ${user.name}? Their login and physical access will be disabled, dependents deactivated, and property unit released. Historical records will be preserved.`
        : `Are you sure you want to delete ${user.name}?`;
    if (window.confirm(message)) {
      deleteUser.mutate(user._id);
    }
  };

  const handleBulkPermission = (user) => {
    setSelectedUser(user);
    setBulkPermissionData({
      approved: true,
      limit: 75,
    });
    setShowBulkPermissionModal(true);
  };

  const handleViewBulkRequests = (user) => {
    setSelectedUser(user);
    setShowBulkRequestsModal(true);
  };

  const handleApproveRequest = (request) => {
    if (
      window.confirm(
        `Approve ${request.numberOfVisitors} visitors for ${selectedUser?.name}?`,
      )
    ) {
      approveBulkRequest.mutate(request._id);
    }
  };

  const handleRejectRequest = (request) => {
    setRejectingRequest(request);
    setRejectReason("");
  };

  const confirmReject = () => {
    if (!rejectingRequest) return;
    rejectBulkRequest.mutate({
      requestId: rejectingRequest._id,
      reason: rejectReason,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      apartment: formData.apartment.trim(),
      estate: formData.estate,
      role: formData.role,
      password: formData.password,
    };

    // Only include userId if provided
    if (formData.userId && formData.userId.trim() !== "") {
      payload.userId = formData.userId.trim();
    }

    if (payload.role === "resident" && (!payload.email || !payload.phone)) {
      toast.error("Residents require both email and phone");
      return;
    }

    if (modalType === "create" && payload.role === "resident") {
      const dependentLimit =
        formData.dependentLimit === "" ? undefined : Number(formData.dependentLimit);

      if (
        dependentLimit !== undefined &&
        (Number.isNaN(dependentLimit) || dependentLimit < 0 || dependentLimit > 4)
      ) {
        toast.error("Dependent count must be between 0 and 4");
        return;
      }

      const registrationPayload = {
        fullName: payload.name,
        preferredUserId: payload.userId || undefined,
        password: payload.password,
        estate: formData.estate,
        unitId: formData.unitId,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        governmentIdNumber: formData.governmentIdNumber.trim(),
        cardNo: formData.cardNo.trim() || undefined,
        profilePhotoUrl: formData.profilePhotoUrl.trim() || undefined,
        mobileNumber: payload.phone,
        emailAddress: payload.email,
        ownershipType: formData.ownershipType,
        moveInDate: formData.moveInDate,
        proofOfResidencyDocumentUrl:
          formData.proofOfResidencyDocumentUrl.trim() || undefined,
        governmentIdDocumentUrl:
          formData.governmentIdDocumentUrl.trim() || undefined,
        emergencyContactName: formData.emergencyContactName.trim() || undefined,
        emergencyContactPhone:
          formData.emergencyContactPhone.trim() || undefined,
        occupation: formData.occupation.trim() || undefined,
        employer: formData.employer.trim() || undefined,
        additionalNotes: formData.additionalNotes.trim() || undefined,
      };

      if (dependentLimit !== undefined) {
        registrationPayload.dependentLimit = dependentLimit;
      }

      createResidentRegistration.mutate(registrationPayload);
      return;
    }

    if (modalType === "create") {
      createUser.mutate(payload);
    } else {
      const updateData = {
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        apartment: payload.apartment,
        estate: payload.estate,
        role: payload.role,
      };

      if (formData.userId && formData.userId.trim() !== "") {
        updateData.userId = formData.userId.trim();
      }

      if (payload.password && payload.password.trim() !== "") {
        updateData.password = payload.password;
      }

      updateUser.mutate(updateData);
    }
  };

  const handleBulkPermissionSubmit = (e) => {
    e.preventDefault();
    updateBulkPermission.mutate({
      userId: selectedUser._id,
      data: bulkPermissionData,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "estate") {
        next.unitId = "";
        next.apartment = "";
      }

      if (name === "unitId") {
        const selectedUnit = propertyUnits.find((unit) => unit._id === value);
        next.apartment = selectedUnit?.unitCode || "";
      }

      return next;
    });
  };

  const handleDocumentUpload = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const response = await residentRegistrationApi.uploadDocument(file);
      const url = response.data?.data?.url;
      if (!url) throw new Error("Upload did not return a file URL");
      setFormData((prev) => ({ ...prev, [fieldName]: url }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to upload file"));
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
      e.target.value = "";
    }
  };

  const handleBulkPermissionChange = (e) => {
    const { name, value, type } = e.target;
    setBulkPermissionData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? e.target.checked : value,
    }));
  };

  // Check if user has any bulk visit requests
  const hasAnyBulkRequests = (userId) => {
    if (!allBulkRequests) return false;
    const requests = Array.isArray(allBulkRequests) ? allBulkRequests : [];
    return requests.some((req) => req.resident?._id === userId);
  };

  // Get pending bulk requests count for a user
  const getPendingRequestsCount = (userId) => {
    if (!allBulkRequests) return 0;
    const requests = Array.isArray(allBulkRequests) ? allBulkRequests : [];
    return requests.filter(
      (req) => req.resident?._id === userId && req.status === "pending",
    ).length;
  };

  // Filter users
  const filteredUsers =
    users?.filter((user) => {
      const matchesSearch =
        user?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.userId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesRole = selectedRole === "all" || user?.role === selectedRole;
      const matchesEstate =
        !selectedEstateId || (user?.estate?._id || user?.estate) === selectedEstateId;
      return matchesSearch && matchesRole && matchesEstate;
    }) || [];

  const getRoleBadge = (role) => {
    const badges = {
      resident: "badge-success",
      security: "badge-warning",
      maintenance: "badge-info",
    };
    return badges[role] || "badge-secondary";
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "badge-warning",
      approved: "badge-success",
      rejected: "badge-danger",
    };
    return badges[status] || "badge-secondary";
  };

  // Estate counts for grouping boxes
  const estateCounts = React.useMemo(() => {
    const counts = {};
    if (Array.isArray(estates)) {
      estates.forEach((e) => {
        counts[e._id] = 0;
      });
    }
    if (Array.isArray(users)) {
      users.forEach((u) => {
        const id = u.estate?._id;
        if (id && counts[id] !== undefined) counts[id]++;
      });
    }
    return counts;
  }, [users, estates]);

  const handleEstateClick = (estate) => {
    setShellEstateId(estate._id);
    setSelectedEstateId(estate._id);
    setSearchTerm("");
    setSelectedRole("all");
  };

  const clearEstateFilter = () => {
    setSelectedEstateId("");
  };

  const isResidentCreate =
    modalType === "create" && formData.role === "resident";
  const isSubmitting =
    createUser.isLoading ||
    updateUser.isLoading ||
    createResidentRegistration.isLoading ||
    Object.values(uploadingFields).some(Boolean);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading users
          </h3>
          <p className="text-gray-600 mb-4">
            Failed to load users. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Residents</h1>
          <p className="text-gray-600">Manage all users in the system</p>
        </div>
        <button
          onClick={handleCreate}
          className="btn btn-primary flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </button>
      </div>

      {/* Estate summary boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.isArray(estates) &&
          estates.map((e) => (
            <div
              key={e._id}
              className={`card cursor-pointer ${selectedEstateId === e._id ? "border-blue-500" : ""
                }`}
              onClick={() => handleEstateClick(e)}
            >
              <div className="card-body text-center">
                <h3 className="font-semibold text-gray-900">{e.name}</h3>
                <p className="text-sm text-gray-600">
                  {estateCounts[e._id] || 0} user
                  {estateCounts[e._id] === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          ))}
      </div>

      {selectedEstateId && (
        <div className="flex items-center space-x-2">
          <span className="text-sm">
            Showing users in{" "}
            <strong>
              {estates?.find((x) => x._id === selectedEstateId)?.name ||
                "selected estate"}
            </strong>
          </span>
          <button
            onClick={clearEstateFilter}
            className="hidden"
          >
            Clear
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 bg-white" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: "40px" }}
              />
            </div>

            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="form-input"
            >
              <option value="all">All Roles</option>
              <option value="resident">Resident</option>
              <option value="security">Security</option>
              <option value="maintenance">Maintenance</option>
            </select>

            <div className="text-sm text-gray-600 flex items-center">
              {filteredUsers?.length || 0} users found
              {selectedEstateId && estates && (
                <span>
                  {" "}
                  in{" "}
                  <strong>
                    {estates.find((x) => x._id === selectedEstateId)?.name}
                  </strong>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Users</h3>
        </div>
        <div className="card-body">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Estate</th>
                  <th>Bulk Requests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(filteredUsers) &&
                  filteredUsers.map((user) => {
                    const pendingCount = getPendingRequestsCount(user._id);
                    return (
                      <tr key={user._id}>
                        <td>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                              <User className="h-4 w-4 text-gray-600" />
                            </div>
                            <span className="font-medium text-gray-900">
                              {user.name}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="font-mono text-gray-700 text-sm">
                            {user.userId || "-"}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-600">{user.email || "-"}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getRoleBadge(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          {user.estate ? (
                            <div className="flex items-center">
                              <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-gray-600">
                                {user.estate.name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">Not assigned</span>
                          )}
                        </td>
                        <td>
                          {hasAnyBulkRequests(user._id) ? (
                            <button
                              onClick={() => handleViewBulkRequests(user)}
                              className="relative inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View Requests
                              {pendingCount > 0 && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {pendingCount} pending
                                </span>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBulkPermission(user)}
                              className="inline-flex items-center text-sm text-green-600 hover:text-green-800"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Create Bulk Request
                            </button>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-1 text-gray-400 hover:text-blue-600"
                              title="Edit user"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleBulkPermission(user)}
                              className="p-1 text-gray-400 hover:text-green-600"
                              title="Set bulk visit permission"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(user)}
                              disabled={deleteUser.isLoading}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title={user.role === "resident" ? "Remove resident" : "Delete user"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {(!filteredUsers || filteredUsers.length === 0) && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No users found
          </h3>
          <p className="text-gray-600 mb-4">
            Get started by creating your first user.
          </p>
          <button onClick={handleCreate} className="btn btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalType === "create"
                  ? isResidentCreate
                    ? "Create Resident"
                    : "Create New User"
                  : "Edit User"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">User ID</label>
                  <input
                    type="text"
                    name="residentUserId"
                    autoComplete="off"
                    value={formData.userId}
                    onChange={(event) => setFormData((current) => ({ ...current, userId: event.target.value }))}
                    className="form-input"
                    placeholder={
                      modalType === "create"
                        ? "Optional (auto-generated if empty)"
                        : "User login ID"
                    }
                  />
                </div>

                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required={formData.role === "resident"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    required={formData.role === "resident"}
                  />
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="resident">Resident</option>
                    <option value="security">Security</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                {!isResidentCreate && (
                  <div>
                    <label className="form-label">Apartment</label>
                    <input
                      type="text"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., A-101"
                    />
                  </div>
                )}
              </div>

              {modalType === "create" && (
                <div>
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    name="residentTemporarySecret"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={(event) => setFormData((current) => ({ ...current, password: event.target.value }))}
                    className="form-input"
                    required
                  />
                </div>
              )}

              <div>
                <label className="form-label">Assign to Estate</label>
                <select
                  name="estate"
                  value={formData.estate}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Estate</option>
                  {Array.isArray(estates) &&
                    estates.map((estate) => (
                      <option key={estate._id} value={estate._id}>
                        {estate.name}
                      </option>
                    ))}
                </select>
              </div>

              {isResidentCreate && (
                <>
                  <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                    The resident will be activated immediately. Select a unit
                    from the estate property register and set the number of
                    dependents they may add.
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={openPropertyUnitRegister}
                        className="btn btn-primary"
                      >
                        Manage Property Units
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          queryClient.invalidateQueries([
                            "residentPropertyUnits",
                          ])
                        }
                        className="btn btn-secondary"
                      >
                        Refresh Units
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Property Unit</label>
                      <select
                        name="unitId"
                        value={formData.unitId}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        disabled={!formData.estate || loadingPropertyUnits}
                      >
                        <option value="">
                          {loadingPropertyUnits
                            ? "Loading units..."
                            : !formData.estate
                              ? "Select Estate First"
                              : availablePropertyUnits.length
                                ? "Select Property Unit"
                                : "No Active Property Units"}
                        </option>
                        {availablePropertyUnits.map((unit) => (
                          <option key={unit._id} value={unit._id}>
                            {unit.unitCode}
                            {unit.displayName ? ` - ${unit.displayName}` : ""}
                            {unit.occupancyStatus
                              ? ` (${unit.occupancyStatus})`
                              : ""}
                          </option>
                        ))}
                      </select>
                      {formData.estate &&
                        !loadingPropertyUnits &&
                        !availablePropertyUnits.length && (
                          <p className="mt-2 text-xs text-amber-700">
                            No active property units were found for this estate.
                            Open the property unit register to add the estate
                            units or verify the unit status and estate
                            assignment.
                          </p>
                        )}
                      {formData.estate &&
                        !loadingPropertyUnits &&
                        !!availablePropertyUnits.length && (
                          <p className="mt-2 text-xs text-slate-500">
                            Final unit availability is validated when the
                            resident is created.
                          </p>
                        )}
                    </div>

                    <div>
                      <label className="form-label">Dependent Count</label>
                      <input
                        type="number"
                        name="dependentLimit"
                        value={formData.dependentLimit}
                        onChange={handleInputChange}
                        className="form-input"
                        min="0"
                        max="4"
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">Ownership Type</label>
                      <select
                        name="ownershipType"
                        value={formData.ownershipType}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="owner">Owner</option>
                        <option value="tenant">Tenant</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">Gender</label>
                      <select
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="form-label">Move-in Date</label>
                      <input
                        type="date"
                        name="moveInDate"
                        value={formData.moveInDate}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Government ID Number</label>
                      <input
                        type="text"
                        name="governmentIdNumber"
                        value={formData.governmentIdNumber}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">Occupation</label>
                      <input
                        type="text"
                        name="occupation"
                        value={formData.occupation}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="form-label">Access Card Number (optional)</label>
                      <input
                        type="text"
                        name="cardNo"
                        value={formData.cardNo}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="CVSecurity card number"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Profile Photo (optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleDocumentUpload(e, "profilePhotoUrl")}
                        className="form-input"
                        disabled={uploadingFields.profilePhotoUrl}
                      />
                      {uploadingFields.profilePhotoUrl && (
                        <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                      )}
                      {formData.profilePhotoUrl && !uploadingFields.profilePhotoUrl && (
                        <a
                          href={formData.profilePhotoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-green-600 mt-1 inline-block"
                        >
                          View uploaded photo
                        </a>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Employer</label>
                      <input
                        type="text"
                        name="employer"
                        value={formData.employer}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Proof of Residency (optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleDocumentUpload(e, "proofOfResidencyDocumentUrl")
                        }
                        className="form-input"
                        disabled={uploadingFields.proofOfResidencyDocumentUrl}
                      />
                      {uploadingFields.proofOfResidencyDocumentUrl && (
                        <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                      )}
                      {formData.proofOfResidencyDocumentUrl &&
                        !uploadingFields.proofOfResidencyDocumentUrl && (
                          <a
                            href={formData.proofOfResidencyDocumentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-green-600 mt-1 inline-block"
                          >
                            View uploaded document
                          </a>
                        )}
                    </div>

                    <div>
                      <label className="form-label">
                        Government ID Document (optional)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleDocumentUpload(e, "governmentIdDocumentUrl")
                        }
                        className="form-input"
                        disabled={uploadingFields.governmentIdDocumentUrl}
                      />
                      {uploadingFields.governmentIdDocumentUrl && (
                        <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                      )}
                      {formData.governmentIdDocumentUrl &&
                        !uploadingFields.governmentIdDocumentUrl && (
                          <a
                            href={formData.governmentIdDocumentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-green-600 mt-1 inline-block"
                          >
                            View uploaded document
                          </a>
                        )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Emergency Contact Name</label>
                      <input
                        type="text"
                        name="emergencyContactName"
                        value={formData.emergencyContactName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="form-label">Emergency Contact Phone</label>
                      <input
                        type="tel"
                        name="emergencyContactPhone"
                        value={formData.emergencyContactPhone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleInputChange}
                      className="form-input min-h-[96px]"
                      placeholder="Optional notes for this resident registration"
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Saving...
                    </>
                  ) : modalType === "create" ? (
                    isResidentCreate ? "Create Resident" : "Create User"
                  ) : (
                    "Update User"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Visit Permission Modal */}
      {showBulkPermissionModal && (
        <div
          className="modal-overlay"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Set Bulk Visit Permission
              </h2>
              <button
                onClick={() => setShowBulkPermissionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {selectedUser && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Setting permission for:{" "}
                  <span className="font-semibold text-gray-900">
                    {selectedUser.name}
                  </span>
                </p>
                <p className="text-xs text-gray-500">{selectedUser.email}</p>
              </div>
            )}

            <form onSubmit={handleBulkPermissionSubmit} className="space-y-4">
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="approved"
                    checked={bulkPermissionData.approved}
                    onChange={handleBulkPermissionChange}
                    className="form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="form-label mb-0">
                    Approve Bulk Visit Permission
                  </span>
                </label>
                <p className="text-sm text-gray-500 mt-1 ml-7">
                  Enable this user to create multiple visit requests at once
                </p>
              </div>

              <div>
                <label className="form-label">Visit Limit</label>
                <input
                  type="number"
                  name="limit"
                  value={bulkPermissionData.limit}
                  onChange={handleBulkPermissionChange}
                  className="form-input"
                  min="1"
                  max="1000"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Maximum number of visits this user can create in bulk (1-1000)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBulkPermissionModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateBulkPermission.isLoading}
                  className="btn btn-primary"
                >
                  {updateBulkPermission.isLoading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Updating...
                    </>
                  ) : (
                    "Update Permission"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Requests Modal */}
      {showBulkRequestsModal && (
        <div
          className="modal-overlay"
        >
          <div
            className="modal"
            style={{ maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Bulk Visit Requests
                </h2>
                {selectedUser && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedUser.name} - {selectedUser.email}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowBulkRequestsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingUserRequests ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : !userBulkRequests || userBulkRequests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Bulk Requests
                </h3>
                <p className="text-gray-600">
                  This user hasn't made any bulk visit requests yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {userBulkRequests.map((request) => (
                  <div
                    key={request._id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {request.numberOfVisitors} Visitors
                          </h3>
                          <span
                            className={`badge ${getStatusBadge(request.status)}`}
                          >
                            {request.status}
                          </span>
                        </div>
                        {request.eventType && (
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Event:</strong> {request.eventType}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>
                          <strong>Start:</strong>{" "}
                          {formatDateTime(request.visitStartTime)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>
                          <strong>End:</strong>{" "}
                          {formatDateTime(request.visitEndTime)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Clock className="h-4 w-4 mr-2" />
                        <span>
                          <strong>Requested:</strong>{" "}
                          {formatDateTime(request.createdAt)}
                        </span>
                      </div>
                    </div>

                    {request.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                          <MessageSquare className="h-4 w-4 text-red-600 mr-2 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-900 mb-1">
                              Rejection Reason:
                            </p>
                            <p className="text-sm text-red-700">
                              {request.rejectionReason}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {request.status === "pending" && (
                      <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => handleRejectRequest(request)}
                          className="flex-1 btn btn-secondary flex items-center justify-center"
                          disabled={
                            approveBulkRequest.isLoading ||
                            rejectBulkRequest.isLoading
                          }
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </button>
                        <button
                          onClick={() => handleApproveRequest(request)}
                          className="flex-1 btn btn-primary flex items-center justify-center"
                          disabled={
                            approveBulkRequest.isLoading ||
                            rejectBulkRequest.isLoading
                          }
                        >
                          {approveBulkRequest.isLoading ? (
                            <>
                              <div className="spinner mr-2"></div>
                              Approving...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Reject Reason Modal */}
            {rejectingRequest && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Reject Request
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Please provide a reason for rejecting this bulk visit
                    request:
                  </p>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="form-input w-full h-24 resize-none"
                    placeholder="Enter rejection reason..."
                    required
                  />
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => {
                        setRejectingRequest(null);
                        setRejectReason("");
                      }}
                      className="flex-1 btn btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmReject}
                      className="flex-1 btn btn-primary"
                      disabled={
                        !rejectReason.trim() || rejectBulkRequest.isLoading
                      }
                    >
                      {rejectBulkRequest.isLoading ? (
                        <>
                          <div className="spinner mr-2"></div>
                          Rejecting...
                        </>
                      ) : (
                        "Confirm Reject"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
